import { generateClient } from 'aws-amplify/api';
import {
  listCategories,
  getCategory
} from '../graphql/queries';
import {
  createCategory,
  updateCategory,
  deleteCategory
} from '../graphql/mutations';

/**
 * Category Service
 * Handles all category-related operations using GraphQL API
 */

const client = generateClient();

export // Staff-only mutations below state authMode: 'userPool' rather than
// inheriting whatever the default is at call time. Reads here are public, so
// the default suits them; only the writes need the signed-in token.
class CategoryService {
  // Fallback categories in case GraphQL fails
  static fallbackCategories = [
    { name: 'FRUITS', description: 'Fresh fruits and seasonal produce', isActive: true, productCount: 0 },
    { name: 'VEGETABLES', description: 'Fresh vegetables and root crops', isActive: true, productCount: 0 },
    { name: 'GRAINS', description: 'Cereals, rice, wheat and grain products', isActive: true, productCount: 0 },
    { name: 'HERBS', description: 'Aromatic herbs and spices', isActive: true, productCount: 0 },
    { name: 'DAIRY', description: 'Milk products and dairy items', isActive: true, productCount: 0 },
    { name: 'PROCESSED', description: 'Processed and packaged farm products', isActive: true, productCount: 0 },
    { name: 'LEAFY_GREENS', description: 'Leafy vegetables and greens', isActive: true, productCount: 0 },
    { name: 'CITRUS', description: 'Citrus fruits and related products', isActive: true, productCount: 0 }
  ];

  /**
   * Get all categories with product counts
   */
  static async getCategories() {
    try {
      console.log('🔄 Fetching categories from GraphQL API...');
      
      const response = await client.graphql({
        query: listCategories,
        variables: {
          limit: 100
        }
      });

      const categories = response.data.listCategories.items || [];
      console.log('✅ Fetched categories:', categories);

      // Calculate product counts for each category
      const categoriesWithCounts = await Promise.all(
        categories.map(async (category) => {
          try {
            const productCount = category.products?.items?.length || 0;
            return {
              ...category,
              productCount
            };
          } catch (error) {
            console.warn(`Failed to get product count for category ${category.id}:`, error);
            return {
              ...category,
              productCount: 0
            };
          }
        })
      );

      return categoriesWithCounts;
    } catch (error) {
      console.error('❌ Error fetching categories from GraphQL:', error);
      console.log('🔄 Falling back to default categories...');
      
      // Fallback to default categories if GraphQL fails
      return this.fallbackCategories.map(cat => ({
        ...cat,
        id: cat.name,
        productCount: 0
      }));
    }
  }

  /**
   * Get active categories only
   */
  static async getActiveCategories() {
    const categories = await this.getCategories();
    return categories.filter(cat => cat.isActive);
  }

  /**
   * Create a new category
   */
  static async createCategory(categoryData) {
    try {
      console.log('🔄 Creating category:', categoryData);
      
      // Validate data
      if (!categoryData.name || !categoryData.name.trim()) {
        throw new Error('Category name is required');
      }

      const input = {
        name: categoryData.name.trim(),
        description: categoryData.description || '',
        isActive: categoryData.isActive ?? true,
        displayOrder: categoryData.displayOrder || 0
      };

      const response = await client.graphql({
        query: createCategory,
        authMode: 'userPool',
        variables: { input }
      });

      console.log('✅ Category created successfully:', response.data.createCategory);
      return response.data.createCategory;
    } catch (error) {
      console.error('❌ Error creating category:', error);
      throw new Error(`Failed to create category: ${error.message}`);
    }
  }

  /**
   * Update an existing category
   */
  static async updateCategory(categoryId, categoryData) {
    try {
      console.log('🔄 Updating category:', categoryId, categoryData);
      
      const input = {
        id: categoryId,
        name: categoryData.name?.trim(),
        description: categoryData.description,
        isActive: categoryData.isActive,
        displayOrder: categoryData.displayOrder
      };

      // Remove undefined fields
      Object.keys(input).forEach(key => {
        if (input[key] === undefined) {
          delete input[key];
        }
      });

      const response = await client.graphql({
        query: updateCategory,
        authMode: 'userPool',
        variables: { input }
      });

      console.log('✅ Category updated successfully:', response.data.updateCategory);
      return response.data.updateCategory;
    } catch (error) {
      console.error('❌ Error updating category:', error);
      throw new Error(`Failed to update category: ${error.message}`);
    }
  }

  /**
   * Delete a category
   */
  static async deleteCategory(categoryId) {
    try {
      console.log('🔄 Deleting category:', categoryId);
      
      const response = await client.graphql({
        query: deleteCategory,
        authMode: 'userPool',
        variables: {
          input: { id: categoryId }
        }
      });

      console.log('✅ Category deleted successfully:', response.data.deleteCategory);
      return response.data.deleteCategory;
    } catch (error) {
      console.error('❌ Error deleting category:', error);
      throw new Error(`Failed to delete category: ${error.message}`);
    }
  }

  /**
   * Get categories formatted for form dropdowns
   */
  static async getCategoriesForForm() {
    const categories = await this.getActiveCategories();
    return categories.map(cat => ({
      value: cat.id,
      label: this.formatCategoryName(cat.name),
      description: cat.description
    }));
  }

  /**
   * Format category name for display
   */
  static formatCategoryName(name) {
    return name.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Get category by name
   */
  static async getCategoryByName(name) {
    const categories = await this.getCategories();
    return categories.find(cat => cat.name === name);
  }

  /**
   * Validate category name
   */
  static validateCategoryName(name) {
    if (!name || !name.trim()) {
      return 'Category name is required';
    }
    
    if (name.length < 2) {
      return 'Category name must be at least 2 characters long';
    }
    
    if (name.length > 50) {
      return 'Category name must be less than 50 characters';
    }
    
    // Check for valid characters (letters, numbers, spaces, underscores)
    if (!/^[a-zA-Z0-9\s_]+$/.test(name)) {
      return 'Category name can only contain letters, numbers, spaces, and underscores';
    }
    
    return null; // No validation errors
  }
}

export default CategoryService;